import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import BottomBox from 'src/components/BottomBox/BottomBox'
import NotesInputField from 'src/components/NotesInputField/NotesInputField'
import SvgCheckFilled from 'src/components/Svgs/SvgCheckFilled'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { FormProps } from 'src/types'
import { css } from 'styled-components'

interface NotesInputBoxProps {
  className?: string
  notesProps: FormProps<string> & {
    puttingNotes?: boolean
    puttingNotesComplete?: boolean
    puttingNotesError?: string
  }
  id?: string
  maxLength?: number
  notesCharacters?: number
  isExpense?: boolean
  collapsible?: boolean
}

const NotesInputBox: React.FC<NotesInputBoxProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)

  const fadeOutAnimationDurationInMs = 5000
  const [showSaving, setShowSaving] = React.useState(false)
  const [showComplete, setShowComplete] = React.useState(false)

  useEffect(() => {
    if (props.notesProps.puttingNotesComplete) {
      setShowSaving(false)
      setShowComplete(true)
      setTimeout(() => {
        setShowComplete(false)
      }, fadeOutAnimationDurationInMs)
    }
  }, [props.notesProps.puttingNotesComplete])

  interface LabelProps {
    loading: boolean
    complete: boolean
  }

  const Label: React.FC<LabelProps> = (labelProps): React.ReactElement => {
    return (
      <div key={'status'}>
        {labelProps.loading ? (
          <div
            css={css`
              display: flex;
              align-items: flex-end;
              gap: 10px;
            `}
          >
            <Trns source={'Notes'} />
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <Trns source={'Saving...'} />
            </span>
          </div>
        ) : labelProps.complete ? (
          <>
            {props.notesProps.puttingNotesError ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-end;
                  gap: 10px;
                `}
              >
                <Trns source={'Notes'} />
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  {props.notesProps.puttingNotesError}
                </span>
              </div>
            ) : (
              <div
                css={css`
                  display: flex;
                  align-items: flex-end;
                  gap: 10px;
                `}
              >
                <Trns source={'Notes'} />
                <span
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    ${getFontCss(
                      14,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.medium,
                    )}
                    @keyframes fadeOut {
                      0%,
                      75% {
                        opacity: 1;
                      }
                      100% {
                        opacity: 0;
                      }
                    }
                    animation: fadeOut ${fadeOutAnimationDurationInMs}ms;
                  `}
                >
                  <SvgCheckFilled />
                  <Trns source={'Saved!'} />
                </span>
              </div>
            )}
          </>
        ) : (
          <Trns source={'Notes'} />
        )}
      </div>
    )
  }

  return (
    <BottomBox
      label={
        <Label
          loading={showSaving || !!props.notesProps.puttingNotes}
          complete={showComplete}
        />
      }
      collapsible={props.collapsible}
      className={props.className}
      isExpense={props.isExpense}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <NotesInputField
        showUpdates={props.notesProps.showUpdates}
        baseValue={props.notesProps.baseValue}
        value={props.notesProps.value}
        onChange={props.notesProps.onChange}
        placeholder={translateSingleString(locale, 'Add a note...')}
        id={props.id}
        maxLength={props.maxLength}
        notesCharacters={props.notesCharacters}
        showSaving={showSaving && props.notesProps.puttingNotes !== undefined}
        setShowSaving={
          props.notesProps.puttingNotes !== undefined
            ? setShowSaving
            : undefined
        }
      />
    </BottomBox>
  )
}

export default NotesInputBox
